import type { ReactNode } from 'react'
import { Notice } from './Toast'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, Outlet, useLocation } from 'react-router'
import { SplitLayout, Wordmark, type HeroCopy } from './Brand'
import { useAuth } from './auth/useAuth'
import { ApiError } from './api/client'
import { getPublicConfig } from './api/endpoints'
import { useTextScale } from './lib/textScale'

export function TextSizeControl() {
  const t = useTextScale()
  return (
    <span className="tools" role="group" aria-label="Text size">
      <button type="button" onClick={t.smaller} disabled={!t.canShrink} aria-label="Smaller text">A−</button>
      <button type="button" onClick={t.larger} disabled={!t.canGrow} aria-label="Larger text">A+</button>
    </span>
  )
}

export function Shell() {
  const { logout } = useAuth()
  const { pathname } = useLocation()
  return (
    <>
      <header className="shell-header">
        <Link className="brand" to="/"><Wordmark /></Link>
        <span className="tools">
          <TextSizeControl />
          <button type="button" onClick={logout}>Sign out</button>
        </span>
      </header>
      <main className="page page-enter" key={pathname}><Outlet /></main>
    </>
  )
}

const HERO: HeroCopy = {
  eyebrow: 'Find where you shine',
  title: 'Your strengths, made visible.',
  text: 'Short, friendly tests and a personal report that shows what you are good at.',
  points: ['Sign in with a code. No password to remember.', 'Your answers save as you go.', 'Lose your connection? Pick up where you left off.'],
  chips: ['Aptitude', 'Interests', 'Your report'],
}

export function PublicShell({ children }: { children: ReactNode }) {
  return (
    <>
      <header className="shell-header">
        <span className="brand"><Wordmark /></span>
        <TextSizeControl />
      </header>
      <SplitLayout hero={HERO}><main className="page page-enter">{children}</main></SplitLayout>
    </>
  )
}

export function RequireAuth() {
  const { authed } = useAuth()
  return authed ? <Outlet /> : <Navigate to="/login" replace />
}

/** Plain-language error with an optional next step; the machine `code` stays out of sight. */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null
  const message = error instanceof ApiError ? friendly(error) : 'Something went wrong. Please try again.'
  return <Notice tone="error">{message}</Notice>
}

function friendly(e: ApiError): string {
  if (e.code === 'NETWORK') return 'You seem to be offline. Check your connection and try again.'
  if (e.status >= 500) return 'Something went wrong on our side. Please try again in a moment.'
  return e.message
}

/** "Didn't get the email?" help with the support address, shown only when the deployment sets SUPPORT_EMAIL. */
export function EmailHelp() {
  const config = useQuery({ queryKey: ['public-config'], queryFn: getPublicConfig, staleTime: Infinity, retry: false })
  const support = config.data?.supportEmail
  return (
    <p className="hint">
      Didn't get it? Check your spam or junk folder, wait a minute, then choose "Send a new code".
      {support ? <> Still nothing? Email <a href={`mailto:${support}`}>{support}</a> and tell us the address you used.</> : null}
    </p>
  )
}
